import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { createApolloClient, createApolloCache } from 'providers/ApolloProvider';
import {
  QUERY_assignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderId,
  QUERY_assignToAgencyTeamMemberFetchAgencyJaneById,
  QUERY_assignToAgencyTeamMemberFetchJanes,
} from 'queries';
import {
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQueryVariables,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQueryVariables,
} from 'types/graphql';

const agencyJaneOrderValidationClient = createApolloClient({ cache: createApolloCache() });
export const agencyJaneOrderValidationQueryConfig = ({
  janeId,
  orderRelayId,
  breakTime,
  onSuccess,
}: {
  janeId: string;
  orderRelayId: string;
  breakTime?: string;
  onSuccess: (
    data: AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  ) => void;
}): UseGQLQueryConfig<
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderIdQueryVariables
> => ({
  key: 'FETCH_AGENCY_TEAM_MEMBER_AVAILABILITY_BY_ORDER_ID',
  query: QUERY_assignToAgencyTeamMemberFetchAgencyJaneAvailabilityByJaneIdOrderId,
  client: agencyJaneOrderValidationClient,
  onSuccess,
  variables: {
    janeId,
    orderRelayId,
    breakTime,
  },
});

const fetchAgencyJaneClient = createApolloClient({ cache: createApolloCache() });
export const fetchAgencyJaneQueryConfig = ({
  janeId,
  onSuccess,
}: {
  janeId: string;
  onSuccess: (data: AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery) => void;
}): UseGQLQueryConfig<
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQuery,
  AssignToAgencyTeamMemberFetchAgencyJaneByIdQueryVariables
> => ({
  query: QUERY_assignToAgencyTeamMemberFetchAgencyJaneById,
  key: 'ASSIGN_TO_AGENCY_TEAM_MEMBER_FETCH_AGENCY_JANE_BY_ID',
  client: fetchAgencyJaneClient,
  onSuccess,
  variables: {
    janeId,
  },
});

const fetchAgencyTeamMembersClient = createApolloClient({ cache: createApolloCache() });
export const fetchTeamMembersQueryConfig = ({
  orderId,
  positionId,
  canB2bAssign,
  communityId,
  breakTime,
}: {
  orderId: string;
  positionId: number;
  canB2bAssign: boolean;
  communityId: number[];
  breakTime?: string;
}): ISSFBehaviorsOptions => ({
  query: QUERY_assignToAgencyTeamMemberFetchJanes,
  key: 'ASSIGN_TO_AGENCY_TEAM_MEMBER_FETCH_JANES',
  paginationType: 'InfiniteScroll',
  pageSize: 12,
  client: fetchAgencyTeamMembersClient,
  responseTransformer: (data) => {
    return { ...data, totalCount: data?.requestAllJanes?.totalCount || 0 };
  },
  payloadTransformer: (variables) => ({
    ...variables,
    withOrderAvailability: canB2bAssign,
    requestFilter: canB2bAssign
      ? undefined
      : {
          byMultipleFields: variables.byMultipleFields,
          byAgencyCommunity: variables.communityId,
        },
    marketplaceFilter: canB2bAssign
      ? {
          byMultipleFields: variables.byMultipleFields,
          byPrimaryCommunity: variables.communityId,
        }
      : undefined,
  }),
  schema: {
    orderId: {
      initialValue: orderId,
    },
    byMultipleFields: {
      initialValue: '',
    },
    byPositionId: {
      initialValue: positionId,
    },
    communityId: {
      initialValue: communityId,
    },
    breakTime: {
      initialValue: breakTime,
    },
  },
});
